import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import Navbar from './navbar';
import Home from './home';
import Services from './services';
import Features from './features';
import Pricing from './pricing';
import Faqs from './faq';
import Contact from './contact';
import Footer from './footer';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

/**
 * Scroll to the section named in the URL fragment.
 *
 * The browser normally does this itself, and on this page it cannot: arriving
 * at "/#faqs" from another page is a full document load, and the browser looks
 * for #faqs the moment the HTML lands -- before React has rendered any of the
 * sections. Finding nothing, it stays at the top and never tries again. That is
 * what every navbar link did from /research or a shared forecast: the address
 * bar changed and the page did not move.
 *
 * Retried across a few frames because the sections mount over more than one:
 * one rAF is enough for the hero, not always for the FAQ further down.
 *
 * The offset is the fixed navbar's height. scrollIntoView alone puts the
 * heading directly under the bar, where it reads as cut off.
 */
const NAVBAR_OFFSET = 80;
const MAX_FRAMES = 30;

const useScrollToHash = () => {
    const { hash } = useLocation();

    useEffect(() => {
        const id = (hash || '').replace('#', '');
        if (!id) return undefined;

        let frames = 0;
        let raf = null;

        const tryScroll = () => {
            const el = document.getElementById(id);
            if (el) {
                const top = el.getBoundingClientRect().top + window.pageYOffset - NAVBAR_OFFSET;
                window.scrollTo({ top, behavior: 'smooth' });
                return;
            }
            // Give up rather than spin: a fragment naming nothing on this page
            // should leave the reader at the top, not in a loop.
            if (frames++ < MAX_FRAMES) raf = requestAnimationFrame(tryScroll);
        };

        raf = requestAnimationFrame(tryScroll);
        return () => { if (raf) cancelAnimationFrame(raf); };
    }, [hash]);
};

const Index = () => {
    document.title = "ForecastNest — keep a record of your AI investment research";

    useScrollToHash();
    
    window.onscroll = function () {
        scrollFunction();
    };

    const scrollFunction = () => {
        const element = document.getElementById("back-to-top");
        if (element) {
            if (document.body.scrollTop > 100 || document.documentElement.scrollTop > 100) {
                element.style.display = "block";
            } else {
                element.style.display = "none";
            }
        }
    };

    const toTop = () => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    };
    return (
        <React.Fragment>
            <div className="layout-wrapper landing">
                <Navbar />
                {/* Order tells the story: what this is, then how it works, then
                    the questions people actually ask. A "today's picks" section
                    used to sit under the hero; it was removed rather than
                    reworded, because a record-keeping tool that opens with a
                    stock call is describing a different product. */}
                <Home />
                <Services />
                <Features />
                {/* Price before the FAQ: "what does it cost" is the question
                    that arrives once somebody knows what the thing does, and
                    several of the answers below refer back to it. */}
                <Pricing />
                <Faqs />
                <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                    <Contact />
                </GoogleReCaptchaProvider>
                <Footer />
                <button onClick={() => toTop()} className="btn btn-danger btn-icon landing-back-top" id="back-to-top">
                    <i className="ri-arrow-up-line"></i>
                </button>
            </div>
        </React.Fragment>
    );
};

export default Index;